import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { PRIMARY_BRAND_LOCKUP, PRIMARY_STRAPLINE } from "@/config/brand";
import { productsByCategory } from "@/config/products";

const explore = [
  { to: "/platform", label: "Platform" },
  { to: "/security", label: "Security & Trust" },
  { to: "/evidence", label: "Trust Centre" },
  { to: "/deployment", label: "Deployment" },
  { to: "/use-cases", label: "Use Cases" },
  { to: "/investors", label: "Company" },
];

const consumerExplore = [
  { to: "/account", label: "LightningFlow Account" },
  { to: "/halo/founding-campaign", label: "Halo Traveller Campaign" },
];

const legalAndSupport = [
  { to: "/terms", label: "LightningFlow Terms" },
  { to: "/privacy", label: "Privacy" },
  { to: "/security", label: "Security & Trust" },
  { to: "/support", label: "LightningFlow Support" },
  { to: "/secureshield/privacy", label: "SecureShield Privacy" },
  { to: "/secureshield/pro-terms", label: "SecureShield Pro Terms" },
  { to: "/secureshield/refunds", label: "SecureShield Refunds" },
  { to: "/secureshield/support", label: "SecureShield Support" },
  { to: "/secureshield/account-deletion", label: "Delete a SecureShield Account" },
  { to: "/halo/terms", label: "Halo SuperVPN Terms" },
  { to: "/halo/privacy", label: "Halo SuperVPN Privacy" },
  { to: "/halo/support", label: "Halo SuperVPN Support" },
  { to: "/halo/account-deletion", label: "Delete a Halo Account" },
];

export default function Footer() {
  return (
    <footer
      className="border-t border-ink-100 bg-white"
      data-testid="site-footer"
    >
      <div className="lf-container py-16 md:py-20">
        <div className="flex flex-col gap-8 border-b border-ink-100 pb-10 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-xl">
            <Link to="/" className="logo flex items-center gap-2.5" data-testid="footer-logo">
              <img src="/brand/lightningflow-logo-blue-20261019.png" width="2172" height="724" alt="LIGHTNINGFLOW" className="select-none" style={{ filter: "brightness(2.4) saturate(.9)" }} draggable={false} />
            </Link>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-ink-500">
              {PRIMARY_BRAND_LOCKUP}. One intelligent platform for secure connectivity, real-time visibility, simpler operations and hardware-accelerated performance.
            </p>
          </div>
          <div className="lg:text-right">
            <div className="lf-eyebrow !block">Contact</div>
            <a href="mailto:contact@lightningflow.ai" className="mt-4 inline-block text-sm text-ink-700 hover:text-ink-900" data-testid="footer-contact-email">contact@lightningflow.ai</a>
            <p className="mt-2 text-xs text-ink-400">Australia · ABN 11 681 334 145</p>
            <button type="button" className="mt-4 text-left text-xs text-ink-500 underline underline-offset-4 hover:text-ink-900" onClick={() => window.dispatchEvent(new Event("lf:privacy-settings"))}>Cookie &amp; analytics choices</button>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-4 lg:gap-x-8">
          {productsByCategory().map((group) => group.items.length ? (
            <nav key={group.category} aria-label={`${group.label} products`}>
              <h2 className="lf-eyebrow">{group.label} products</h2>
              <ul className="mt-5 space-y-3">
                {group.items.map((product) => <li key={product.slug}><Link to={`/${product.slug}`} className="text-sm text-ink-700 hover:text-ink-900" data-testid={`footer-product-${product.slug}`}>{product.label}</Link></li>)}
              </ul>
            </nav>
          ) : null)}
          <nav aria-label="Explore LightningFlow">
            <h2 className="lf-eyebrow">Explore</h2>
            <ul className="mt-5 space-y-3">
              {explore.map((l) => <li key={l.label}><Link to={l.to} className="text-sm text-ink-700 hover:text-ink-900" data-testid={`footer-explore-${l.label.toLowerCase().replaceAll(" ", "-")}`}>{l.label}</Link></li>)}
            </ul>
            <ul className="mt-5 space-y-3 border-t border-ink-100 pt-5">
              {consumerExplore.map((l) => <li key={l.label}><Link to={l.to} className="text-sm text-ink-700 hover:text-ink-900" data-testid={`footer-consumer-${l.label.toLowerCase().replaceAll(" ", "-")}`}>{l.label}</Link></li>)}
            </ul>
          </nav>
          <nav aria-label="Legal and support">
            <h2 className="lf-eyebrow">Legal &amp; Support</h2>
            <ul className="mt-5 space-y-3">
              {legalAndSupport.slice(0, 4).map((l) => <li key={l.label}><Link to={l.to} className="text-sm text-ink-700 hover:text-ink-900" data-testid={`footer-legal-${l.label.toLowerCase().replaceAll(" ", "-")}`}>{l.label}</Link></li>)}
            </ul>
            <div className="mt-5 border-t border-ink-100">
              {[{name: "SecureShield", links: legalAndSupport.slice(4, 9)}, {name: "Halo SuperVPN", links: legalAndSupport.slice(9)}].map(({name, links}) => <details key={name} className="group border-b border-ink-100">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-4 text-sm font-medium text-ink-700 hover:text-ink-900 [&::-webkit-details-marker]:hidden">{name}<ChevronDown className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" aria-hidden="true" /></summary>
                <ul className="space-y-3 pb-5 pl-3">{links.map((l) => <li key={l.label}><Link to={l.to} className="text-sm text-ink-600 hover:text-ink-900" data-testid={`footer-legal-${l.label.toLowerCase().replaceAll(" ", "-")}`}>{l.label}</Link></li>)}</ul>
              </details>)}
            </div>
          </nav>
        </div>

        <div className="mt-10 pt-6 border-t border-ink-100 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-400">
            © {new Date().getFullYear()} LightningFlow. All rights reserved.
          </p>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-400">
            LightningFlow · {PRIMARY_STRAPLINE}
          </p>
        </div>
      </div>
    </footer>
  );
}
